import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { syncLineProperty as syncCartLineProperty, isForeignVariantUpdate } from '@theme/aaa-line-property';

/**
 * Custom element wrapper for the availability line block.
 *
 * The block re-renders server-side on variant change because Horizon fetches
 * the product information section with the new ?variant=ID and dispatches a
 * `variant:update` event whose `event.detail.data.html` carries the parsed new
 * DOM. We:
 *
 *   1. Listen for that event on the closest section.
 *   2. Find the new <aaa-availability-line> in event.detail.data.html.
 *   3. Morph our children to match.
 *   4. Re-run the cart line property injection so the hidden input on the
 *      buy form reflects the new variant's lead time.
 *
 * Both steps matter. The morph updates the visible copy. The property sync
 * keeps the cart in agreement with what the customer sees.
 */
class AaaAvailabilityLine extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    // Initial sync of the hidden form property for the SSR-rendered variant.
    this.syncLineProperty();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  /**
   * Handles the variant:update event by morphing in the new server-rendered
   * markup and re-syncing the form line property.
   * @param {CustomEvent} event
   */
  handleVariantUpdate = (event) => {
    // Ignore events from a different product on the same page (rare, but
    // possible with the sticky add-to-cart or quick-add).
    if (isForeignVariantUpdate(event, this)) return;

    const newHtml = event.detail?.data?.html;
    if (!newHtml) return;

    const replacement = newHtml.querySelector('aaa-availability-line');
    if (!replacement) return;

    // Copy attributes (status class, data-*, etc.) and morph children.
    for (const attr of Array.from(replacement.attributes)) {
      if (this.getAttribute(attr.name) !== attr.value) {
        this.setAttribute(attr.name, attr.value);
      }
    }
    morph(this, replacement, { childrenOnly: true });

    this.syncLineProperty();
  };

  /**
   * Writes (or removes) the `properties[Key]` hidden input on every product
   * form on the page that belongs to this product. Called on first connect
   * and on every variant change.
   *
   * The DOM work lives in `assets/aaa-line-property.js` so this block and
   * `aaa-shipping-duty-prompt` (pickup-only mode) share one implementation.
   * The data-* contract is documented there.
   */
  syncLineProperty = () => {
    syncCartLineProperty(this, this.section);
  };
}

if (!customElements.get('aaa-availability-line')) {
  customElements.define('aaa-availability-line', AaaAvailabilityLine);
}
